import React, { useState, useEffect, useMemo, useContext, useCallback, FC } from 'react';
import axios from "axios";
import { Typography, ListBase, useSnackbar, ThemeProvider, Paper, Box, ListSearchChangeProps, DataTable, OnSelectAllChangeEvent } from '@pixel/core';
import Tooltip from '@pixel/core/Tooltip';
import Link from '@pixel/core/Link';
import CircularProgress from '@pixel/core/CircularProgress';
import { createInternalTheme } from "@pixel/internal/styles";
import { LayoutRoot, LayoutRootSection } from '@pixel/internal';
import TableChartIcon from '@pixel/assets/icons/TableChart';
import { useMultipleLocalizedText } from '../../../../contexts/Localization/localizationHooks';
import { 
    AssociationType,
    IBUSearchGetParms,
    IBreadcrumbHierarchyObject,
    IBusinessData, IBusinessGetParms,
    IBusinessResultData,
    IDimensionsProps,
    ISelectedDimensionsData,
    IStoredMappingData,
 } from '../../OdsTypes';
import OdsContext from '../../OdsContext';
import { getFullySelectedKeys, getLocalStorageDimensionData, getPartiallySelectedDimensionIds, getSelectedSubDimensionsCount, isAvailableInBreadcrumb, getPartiallySelectedModifiedIds, mergePreselectedAndActiveDimensions, setLocalStorageDimensionData, transformBUResultData, updateBreadcrumb, updateDimensionLocalMapList, updateLocalSelectionState } from '../../OdsTransform';
import { SelectChangeEvent } from '@mui/material';
import { AddDimensionModalBreadcrumb } from '../modal/AddDimensionModalBreadcrumb';

const BusinessUnit: FC<IDimensionsProps> = ({
    accessGroupData,
    selectedBusinessUnitForAPIPayload,
    setSelectedBusinessUnitForAPIPayload,
    setCurrentDimensionSelectionList,
}) => {

    const OrgElementsContext = useContext(OdsContext);
    const {
        ODS_SELECTED,
        ODS_TAB_BUSINESS_UNITS,
        ODS_SUB_BUSINESS_UNITS,
        ODS_DESCRIPTION, ODS_STATUS,
        ODS_SEARCH_PLACEHOLDER,
        ODS_SEARCH_BUSINESS_UNITS,
        ODS_ROWS_PER_PAGE_LABEL,
        I18N_NO_RESULTS_FOUND,
        ODS_PARENT_BUSINESS_UNIT,
    } = useMultipleLocalizedText();
    const theme = createInternalTheme({ darkMode: false, theme: {} });

    /*** styling */
    const jvBusinessTitle = {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        wordBreak: 'break-all',
        whiteSpace: 'nowrap',
        display: 'inline-block',
    }

    const overlayStyling = {
        display: 'flex',
        justifyContent: 'center',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        alignItems: 'center',
        zIndex: '1',
    }
    /*** End styling */

    const DEFAULT_STATUS = 'ACTIVE';
    const ODSPageSizeSessionStorage = () => JSON.parse(sessionStorage.getItem("ODS_PageSize")) ? JSON.parse(sessionStorage.getItem("ODS_PageSize")) : OrgElementsContext.defaultPageSize;
    const snackbar = useSnackbar();
    const partiallySelectedBusinessUnitIds = getPartiallySelectedDimensionIds('AG_businessUnit_list');

    /** memoized */
    const BUFetchParams: IBusinessGetParms = useMemo(() => ({
        pageSize: ODSPageSizeSessionStorage(),
        pageNumber: 0,
        status: DEFAULT_STATUS,
    }), []);
    const BUSearchParams: IBUSearchGetParms = useMemo(() => ({
        pageSize: ODSPageSizeSessionStorage(),
        pageNumber: 0,
        keywords: '',
    }), []);
    /** end memoized */

    /*** state variables  */
    const [BUFetchPayload, setBUFetchPayload] = useState<IBusinessGetParms>(BUFetchParams);
    const [BUSearchPayload, setBUSearchPayload] = useState<IBUSearchGetParms>(BUSearchParams);
    const [results, setResults] = useState<IBusinessResultData>({ nodes: [], totalCount: 0, pageNumber: 0, pageSize: ODSPageSizeSessionStorage() });
    const [BUHierarchyTree, setBUHierarchyTree] = useState([{ name: 'All', eid: '' }]);
    const [perPageSize, setPerPageSize] = useState(ODSPageSizeSessionStorage());
    const [listLoading, setListLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [noSearchResult, setNoSearchResult] = useState(false);
    const [showInProgress, setShowInProgress] = useState(false);
    const [pagingContext, setPagingContext] = useState({
        hideFirstLastActions: true,
        perPage: perPageSize,
        page: 1,
        first: 1,
        last: perPageSize,
    });
    const [selectedBusinessUnits, setSelectedBusinessUnits] = useState<Array<ISelectedDimensionsData['eid']>>([])
    /*** end state variables */

    /*** use effects */
    useEffect(() => {
        fetchBusinessUnitList(BUFetchPayload, accessGroupData ? accessGroupData.eid : null);
        setSearchText('');
    }, [BUFetchPayload]);

    useEffect(() => {
        if (searchText && searchText.length > 1) {
            const accessGroupEid = accessGroupData ? accessGroupData.eid : null;
            const modifiedPartiallySelectedIds : Array<string> = getPartiallySelectedModifiedIds(partiallySelectedBusinessUnitIds, 'AG_businessUnit_list');
            const bodyPayload = {
                modifiedPartiallySelectedEIds: modifiedPartiallySelectedIds
            }
            axios.post(window.jvUrls.admin + '/action/accessGroups/businessUnits/search/' + accessGroupEid, bodyPayload, {
                params: BUSearchPayload
            })
                .then(function (response) {
                    const activeBusinessUnits = response?.data?.activeBusinessUnits;
                    setResults(transformBUResultData(activeBusinessUnits));
                    setSelectedBusinessUnits(updateDimensionLocalMapList(response?.data?.associatedBusinessUnitsArray, 'AG_businessUnit_list', response.data.childrenForModifiedPartiallySelectedRequestedIds));
                    mergePreselectedAndActiveDimensions(activeBusinessUnits?.businessUnits, 'namedBusinessUnitsData');
                    setCurrentDimensionSelectionList(getLocalStorageDimensionData('AG_businessUnit_list'));
                    setPagingContext({
                        hideFirstLastActions: true,
                        perPage: activeBusinessUnits?.pageSize,
                        page: activeBusinessUnits?.pageNumber + 1,
                        first: activeBusinessUnits?.businessUnits?.length > 0 ? activeBusinessUnits?.pageNumber * activeBusinessUnits?.pageSize + 1 : 0,
                        last: activeBusinessUnits?.pageSize * activeBusinessUnits?.pageNumber + activeBusinessUnits?.businessUnits?.length,
                    });
                    setListLoading(false);
                    activeBusinessUnits?.totalCount === 0 ? setNoSearchResult(true) : setNoSearchResult(false);
                })
                .catch(function (error) {
                    console.log(error);
                    setListLoading(false);
                });
        }
    }, [searchText, BUSearchPayload]);
    /*** End use effects */

    /*** xhr side effects */
    const fetchBusinessUnitList = async (BUFetchPayload: IBusinessGetParms, accessGroupEid: string) => {
        try {
            setListLoading(true);
            const modifiedPartiallySelectedIds : Array<string> = getPartiallySelectedModifiedIds(partiallySelectedBusinessUnitIds, 'AG_businessUnit_list');
            const bodyPayload = {
                modifiedPartiallySelectedEIds: modifiedPartiallySelectedIds
            }
            await axios.post(window.jvUrls.admin + '/action/accessGroups/businessUnits/' + accessGroupEid, bodyPayload, {
                params: BUFetchPayload
            })
                .then((response) => {
                    const activeBusinessUnits = response?.data?.activeBusinessUnits;
                    const { businessUnits, pageSize, pageNumber } = activeBusinessUnits;

                    setResults(transformBUResultData(activeBusinessUnits));
                    setSelectedBusinessUnits(updateDimensionLocalMapList(response.data.associatedBusinessUnitsArray, 'AG_businessUnit_list', response.data.childrenForModifiedPartiallySelectedRequestedIds));
                    mergePreselectedAndActiveDimensions(activeBusinessUnits?.businessUnits, 'namedBusinessUnitsData');
                    setCurrentDimensionSelectionList(getLocalStorageDimensionData('AG_businessUnit_list'));
                    if (BUHierarchyTree.length === 1) {
                        sessionStorage.setItem("BU_company", JSON.stringify(businessUnits[0]));
                    }
                    businessUnits.length > 0 && setNoSearchResult(false);
                    setPagingContext({
                        hideFirstLastActions: true,
                        perPage: pageSize,
                        page: pageNumber + 1,
                        first: businessUnits.length > 0 ? pageNumber * pageSize + 1 : 0,
                        last: pageSize * pageNumber + businessUnits.length,
                    });
                    setListLoading(false);
                })
                .catch((error) => {
                    // show snackbar here
                    setListLoading(false);
                });
        } catch (error) {
            // show snackbar here
            console.log(error);
        }
    }

    const updatePayload = (business: IBreadcrumbHierarchyObject) => {
        if (business.eid === '') {
            setBUFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, status: DEFAULT_STATUS });
        } else {
            setBUFetchPayload({ ...BUFetchPayload, parentEId: business.eid, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, status: DEFAULT_STATUS });
        }
    }
    /*** end xhr side effects */

    /*** event handlers */
    const onBreadcrumChange = (business: IBreadcrumbHierarchyObject) => {
        setBUHierarchyTree(updateBreadcrumb(BUHierarchyTree, business));
        updatePayload(business);
    }

    const resetSearch = () => {
        if(searchText !== '') {
            setBUHierarchyTree([{ name: 'All', eid: '' }]);
            setBUFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, status: DEFAULT_STATUS });
        }
    }

    const handleSearch = (props: ListSearchChangeProps) => {
        setSearchText(props.q);
        if (props.q.length > 1) {
            setBUSearchPayload({ ...BUSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, keywords: encodeURIComponent(props.q) })
            setListLoading(true);
        } else {
            setNoSearchResult(false);
            resetSearch();
        }
    };

    const onHandleBUClick = (business: IBreadcrumbHierarchyObject) => {
        const newEntry = { name: business.name, eid: business.eid };
        setSearchText('');
        !isAvailableInBreadcrumb(BUHierarchyTree, business) && setBUHierarchyTree((BUHierarchyTree) => [...BUHierarchyTree, newEntry]);
        setBUFetchPayload({ ...BUFetchPayload, parentEId: business.eid, pageNumber: 0, pageSize: ODSPageSizeSessionStorage(), status: DEFAULT_STATUS });
    }

    const onPageChanged = (event: any, currPage: number) => {
        if (searchText.length > 1) {
            setBUSearchPayload({ ...BUSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: currPage - 1 });
        } else {
            if (BUHierarchyTree[BUHierarchyTree.length - 1].eid === '') {
                setBUFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: currPage - 1, status: DEFAULT_STATUS });
            } else {
                setBUFetchPayload({ ...BUFetchPayload, pageSize: ODSPageSizeSessionStorage(), parentEId: BUHierarchyTree[BUHierarchyTree.length - 1].eid, pageNumber: currPage - 1, status: DEFAULT_STATUS });
            }
        }
    }

    const onPaginationChange = (event: SelectChangeEvent) => {
        sessionStorage.setItem("ODS_PageSize", JSON.stringify(Number(event.target.value)));
        setPerPageSize(Number(event.target.value));
        if (searchText !== '') {
            setBUSearchPayload({ ...BUSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, keywords: encodeURIComponent(searchText) })
            setListLoading(true);
        } else {
            updatePayload(BUHierarchyTree[BUHierarchyTree.length - 1]);
        }
    }

    const onHandleSearchedBUClick = (business: IBreadcrumbHierarchyObject, BUTree: Array<IBreadcrumbHierarchyObject>) => {
        let parentCompany = JSON.parse(sessionStorage.getItem("BU_company"));
        parentCompany.eid === business.eid && BUTree.length == 2 && BUTree.pop();
        const newEntry = [{ name: 'All', eid: '' }, ...BUTree];
        setSearchText('');
        setBUHierarchyTree(newEntry);
        setBUFetchPayload({ ...BUFetchPayload, parentEId: business.eid, pageNumber: 0, pageSize: ODSPageSizeSessionStorage(), status: DEFAULT_STATUS });
    }

    useEffect(() => {
        const modifiedSelectedBusinessUnits: Array<ISelectedDimensionsData> = [];
        selectedBusinessUnits?.forEach((value: string) => {
            modifiedSelectedBusinessUnits.push({eid: value, associationType: AssociationType.FULLY_ASSOCIATED});
        });
        setSelectedBusinessUnitForAPIPayload(modifiedSelectedBusinessUnits);
        selectedBusinessUnits?.length === 0 && setSelectedBusinessUnitForAPIPayload([]);
    }, [selectedBusinessUnits]);

    const handleSelection = useCallback((node: ISelectedDimensionsData, nextSelectedState: boolean) => {
        const prevAssociatedData: IStoredMappingData = getLocalStorageDimensionData('AG_businessUnit_list');

        if (nextSelectedState) {
            setSelectedBusinessUnits([...selectedBusinessUnits, node.eid])
        } else {
            setSelectedBusinessUnits(selectedBusinessUnits.filter(item => item !== node.eid))
        }
        setSelectedBusinessUnits(updateLocalSelectionState(node.eid, nextSelectedState, 'AG_businessUnit_list', prevAssociatedData));
    }, [selectedBusinessUnits])

    /**
    * handleBusinessUnitSelectAll Manipulates the click event to selectAll list items
    *
    * @param {any} event - event object
    * @param {any} eventType - eventType - selectAll, unselectAll, etc
    * @param {any} context - context of the event
    */
    const handleBusinessUnitSelectAll: OnSelectAllChangeEvent = (event: any, eventType: any, context: any) => {
        let localList: IStoredMappingData = getLocalStorageDimensionData('AG_businessUnit_list');
        localList.dimensionIdToDimensionList = localList.dimensionIdToDimensionList || {};
        for (const key in localList.dimensionIdToDimensionList) {
            if (eventType === 'selectAll') {
                localList.dimensionIdToDimensionList[key].associationType = AssociationType.FULLY_ASSOCIATED;
                localList.dimensionIdToDimensionList[key].selectionCount = localList.dimensionIdToDimensionList[key].totalCount;
            } else if (eventType === 'unselectAll') {
                localList.dimensionIdToDimensionList[key].associationType = AssociationType.NOT_ASSOCIATED;
                localList.dimensionIdToDimensionList[key].selectionCount = 0;
            }
        }
        setLocalStorageDimensionData('AG_businessUnit_list', localList);
        setSelectedBusinessUnits(getFullySelectedKeys([localList]));
    }

    /*** end event handlers */

    /*** render helpers */
    const populateTitle = (node: IBusinessData) => {
        if (node.subChildrenCount > 0) {
            return <Tooltip title={node.name} arrow placement="bottom"><Link underline="hover" sx={{ ...jvBusinessTitle, maxWidth: '100%', fontWeight: '600' }} onClick={() => (node.ancestors && searchText.length > 1) ? onHandleSearchedBUClick({ name: node.name, eid: node.eid }, [...node.ancestors, { name: node.name, eid: node.eid }]) : onHandleBUClick(node)} variant="body2">{node.name}</Link></Tooltip>;
        } else {
            return <Tooltip title={node.name} arrow placement="bottom"><span><Typography variant="body2" sx={{ ...jvBusinessTitle, maxWidth: '100%' }} disabled={!node.active}>{node.name}</Typography></span></Tooltip>;
        }
    }

    const renderParentBu = (node: IBusinessData) => {
        const list: any = [];
        if (node.ancestors && node.ancestors.length > 0) {
            node.ancestors.map((parent, index) => {
                list.push(
                    <span style={{ display: 'inline-flex' }}>
                        <Tooltip key={parent.eid} title={parent.name} arrow placement="bottom"><Link underline="hover" sx={{ ...jvBusinessTitle, fontWeight: '600', maxWidth: '140px' }} onClick={() => onHandleSearchedBUClick({ name: parent.name, eid: parent.eid }, node.ancestors)} variant="body2">{parent.name}</Link></Tooltip>
                        <span style={{ padding: '0 6px' }}>{index < node.ancestors.length - 1 && ' /   '}</span>
                    </span>
                );
            })
        }
        return list;
    }

    /*** end render helpers */

    const columns = useMemo(() => [
        { id: 'BusinessUnits', columnName: ODS_TAB_BUSINESS_UNITS, maxWidth: 250, renderCell: (node: IBusinessData) => populateTitle(node) },
        { id: 'SubBusinessUnitsSelected', columnName: `${ODS_SUB_BUSINESS_UNITS || ''} ${ODS_SELECTED || ''}`, renderCell: (node: ISelectedDimensionsData) => getSelectedSubDimensionsCount(node?.eid, 'AG_businessUnit_list'), minWidth: 80 },
        { id: 'SubBusinessUnits', columnName: ODS_SUB_BUSINESS_UNITS || '', renderCell: (node: IBusinessData) => node.subChildrenCount, minWidth: 80 },
    ], []);

    return (
        <React.StrictMode>
            <ThemeProvider theme={theme}>
                <LayoutRoot>
                    <LayoutRootSection top bottom transparent className='jv-tab-panel-wrapper'>
                            <AddDimensionModalBreadcrumb 
                                DimensionHierarchyTree={BUHierarchyTree} 
                                onBreadcrumChange={onBreadcrumChange}
                                searchText={searchText}
                            />
                            {(showInProgress && <Box sx={overlayStyling}><CircularProgress size={60} /></Box>)}
                            <Box className='content-wrapper'>
                                <ListBase
                                    className
                                    showSearch
                                    labelSearch={ODS_SEARCH_PLACEHOLDER}
                                    labelSearchAria={ODS_SEARCH_BUSINESS_UNITS}
                                    // @ts-ignore
                                    results={results} // has the pager content
                                    selectable
                                    selectedIds={selectedBusinessUnits}
                                    partiallySelectedIds={partiallySelectedBusinessUnitIds}
                                    onSelection={handleSelection}
                                    allSelected={selectedBusinessUnits?.length === results?.nodes?.length}
                                    onSearch={handleSearch}
                                    appliedSearchQuery={searchText}
                                    pagingContext={pagingContext}
                                    onPageChanged={onPageChanged}
                                    onPaginationChange={(event: SelectChangeEvent) => onPaginationChange(event)}
                                    rowsPerPageLabel={ODS_ROWS_PER_PAGE_LABEL}
                                    resultsPerPage={perPageSize}
                                    perPageOptions={OrgElementsContext.perPageSelectOptions}
                                    viewContexts={[
                                        {
                                            ariaLabel: 'Datatable View',
                                            icon: <TableChartIcon />,
                                            renderResults: (viewContextProps) => {
                                                if (listLoading) {
                                                    return (<div className={'bu-list-loader'} style={{ display: 'flex', justifyContent: 'center' }}>
                                                        <CircularProgress size={34} />
                                                    </div>)
                                                } else {
                                                    if (!noSearchResult) {
                                                        return (
                                                            <DataTable
                                                                {...viewContextProps}
                                                                getRowID={(node: IBusinessData) => node?.eid}
                                                                columns={columns}
                                                                labelCheckboxAriaLabel={ODS_SELECTED || ''}
                                                                labelCheckboxKey='eid'
                                                            />
                                                        )
                                                    } else {
                                                        return (<div className={'bu-list-no-results'}><Paper sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}> {I18N_NO_RESULTS_FOUND}</Paper></div>)
                                                    }
                                                }
                                            }
                                        }
                                    ]}
                                />
                            </Box>
                    </LayoutRootSection>
                </LayoutRoot>
            </ThemeProvider>
        </React.StrictMode>
    )
}

export default BusinessUnit;
